// Scope: stores bounded, session-persisted chat workspaces, drafts, and unread counts.

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const appendBounded = (items, item, maximum) => {
    if (item?.id && items.some((existing) => existing.id === item.id)) return items;
    return [...items, item].slice(-maximum);
};

const withoutKey = (object, key) => {
    const next = { ...object };
    delete next[key];
    return next;
};

const useChatRoomStore = create(persist((set) => ({
    directChats: [],
    activeDirectChatId: null,
    directMessages: {},
    joinedRooms: [],
    rooms: [],
    roomMessages: [],
    activeRoom: null,
    unreadCounts: {},
    drafts: {},
    createRoom: false,
    isPrivateRoom: false,
    activeRoomPrivate: false,

    setIsPrivateRoom: (value) => set({ isPrivateRoom: value }),
    setCreateRoom: (value) => set({ createRoom: value }),

    openDirectChat: (chat) => set((state) => ({
        directChats: state.directChats.some((item) => item.id === chat.id)
            ? state.directChats
            : [...state.directChats, chat],
        activeDirectChatId: chat.id,
        directMessages: state.directMessages[chat.id]
            ? state.directMessages
            : { ...state.directMessages, [chat.id]: [] },
    })),
    setActiveDirectChat: (chatId) => set({ activeDirectChatId: chatId }),
    appendDirectMessage: (chatId, message) => set((state) => ({
        directMessages: {
            ...state.directMessages,
            [chatId]: appendBounded(state.directMessages[chatId] || [], message, 200),
        },
    })),
    closeDirectChat: (chatId) => set((state) => {
        const directChats = state.directChats.filter((chat) => chat.id !== chatId);
        return {
            directChats,
            directMessages: withoutKey(state.directMessages, chatId),
            drafts: withoutKey(state.drafts, `direct:${chatId}`),
            unreadCounts: withoutKey(state.unreadCounts, `direct:${chatId}`),
            activeDirectChatId: state.activeDirectChatId === chatId
                ? directChats.at(-1)?.id || null
                : state.activeDirectChatId,
        };
    }),
    clearDirectChats: () => set((state) => ({
        directChats: [],
        activeDirectChatId: null,
        directMessages: {},
        drafts: Object.fromEntries(Object.entries(state.drafts).filter(([key]) => !key.startsWith("direct:"))),
        unreadCounts: Object.fromEntries(Object.entries(state.unreadCounts).filter(([key]) => !key.startsWith("direct:"))),
    })),

    appendRoomMessage: (message) => set((state) => ({
        roomMessages: appendBounded(state.roomMessages, message, 500),
    })),
    clearRoomMessages: () => set({ roomMessages: [] }),
    clearRoomMessagesFor: (roomId) => set((state) => ({
        roomMessages: state.roomMessages.filter((message) => message.roomId !== roomId),
    })),

    setRooms: (rooms) => set({ rooms: rooms || [] }),
    addRoom: (newRoom) => set((state) => {
        const incoming = Array.isArray(newRoom) ? newRoom : [newRoom];
        const rooms = [...state.rooms];
        incoming.forEach((room) => {
            if (!rooms.some((existing) => existing.id === room.id)) rooms.push(room);
        });
        return { rooms };
    }),
    deleteRoom: (roomId) => set((state) => ({
        rooms: state.rooms.filter((room) => room.id !== roomId),
        joinedRooms: state.joinedRooms.filter((room) => room.id !== roomId),
        activeRoom: state.activeRoom?.id === roomId ? null : state.activeRoom,
        roomMessages: state.roomMessages.filter((message) => message.roomId !== roomId),
        drafts: withoutKey(state.drafts, `room:${roomId}`),
        unreadCounts: withoutKey(state.unreadCounts, `room:${roomId}`),
    })),
    joinRoomTab: (room) => set((state) => ({
        joinedRooms: state.joinedRooms.some((item) => item.id === room.id)
            ? state.joinedRooms
            : [...state.joinedRooms, room],
        activeRoom: room,
    })),
    leaveRoomTab: (roomId) => set((state) => {
        const joinedRooms = state.joinedRooms.filter((room) => room.id !== roomId);
        return {
            joinedRooms,
            activeRoom: state.activeRoom?.id === roomId ? joinedRooms.at(-1) || null : state.activeRoom,
            roomMessages: state.roomMessages.filter((message) => message.roomId !== roomId),
            drafts: withoutKey(state.drafts, `room:${roomId}`),
            unreadCounts: withoutKey(state.unreadCounts, `room:${roomId}`),
        };
    }),
    setActiveRoom: (room) => set({ activeRoom: room }),
    setActiveRoomPrivate: (value) => set({ activeRoomPrivate: value }),

    setDraft: (workspace, value) => set((state) => ({ drafts: { ...state.drafts, [workspace]: value } })),
    incrementUnread: (workspace) => set((state) => ({
        unreadCounts: { ...state.unreadCounts, [workspace]: (state.unreadCounts[workspace] || 0) + 1 },
    })),
    clearUnread: (workspace) => set((state) => ({
        unreadCounts: { ...state.unreadCounts, [workspace]: 0 },
    })),
    clearWorkspaceState: () => set({
        directChats: [], activeDirectChatId: null, directMessages: {}, joinedRooms: [],
        roomMessages: [], activeRoom: null, unreadCounts: {}, drafts: {},
    }),
}), {
    name: "portfolio-chat-workspace",
    storage: createJSONStorage(() => sessionStorage),
    partialize: (state) => ({
        directChats: state.directChats,
        activeDirectChatId: state.activeDirectChatId,
        directMessages: state.directMessages,
        joinedRooms: state.joinedRooms,
        roomMessages: state.roomMessages,
        activeRoom: state.activeRoom,
        unreadCounts: state.unreadCounts,
        drafts: state.drafts,
    }),
}));

export default useChatRoomStore;
